import styled, { AnyStyledComponent } from "styled-components";
import { IconButton } from "@evergis/ui";

import { ChatMessage } from "./types";

export const ChatContainer = styled.div<{ width?: number }>`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: absolute;
  top: 0.75rem;
  bottom: 0.75rem;
  right: 0.75rem;
  width: ${({ width }) => `${width}px`};
  background-color: ${({ theme: { palette } }) => palette.panelBackground};
  backdrop-filter: blur(20px);
  box-shadow: 0 0.25rem 0.5rem 0 #0000000f;
  border-radius: 0.5rem;
  box-sizing: border-box;
  padding: 1.5rem;
  z-index: 99;
`;

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  button {
    width: auto;
  }
`;

export const ChatTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const ChatTitle = styled.span`
  font-family: "Roboto Light", serif;
  font-weight: 300;
  font-size: 20px;
  line-height: 100%;
  letter-spacing: -0.1px;
  color: ${({ theme: { palette } }) => palette.textPrimary};
`;

export const ChatBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: scroll;
  box-sizing: border-box;
`;

export const ChatMessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  box-sizing: border-box;
`;

export const ChatHelloMessage = styled.div`
  display: flex;
  height: calc(100% - 82px);
  align-items: end;
  justify-content: center;
  font-family: "Roboto Light", serif;
  font-weight: 300;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: -0.1px;
  color: ${({ theme: { palette } }) => palette.textPrimary};
`;

export const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export const ChatMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: calc(100% - 3rem);
  border-radius: 0.5rem;
  font-weight: 400;
  font-size: 14px;
  line-height: normal;
  letter-spacing: 0;
  box-sizing: border-box;

  .wmde-markdown {
    background-color: transparent;
    color: ${({ theme: { palette } }) => palette.textPrimary};

    * {
      font-size: 14px;
      line-height: normal;
      letter-spacing: 0;
    }

    pre,
    th,
    td {
      color: initial;
    }

    .anchor {
      display: none;
    }
  }
`;

export const ChatMessageWrapper = styled.div<Pick<ChatMessage, "isSelf">>`
  display: flex;
  align-items: flex-start;
  justify-content: ${({ isSelf }) => (isSelf ? "flex-end" : "flex-start")};
  gap: 1rem;

  ${ChatMessageContainer} {
    padding: 1rem ${({ isSelf }) => (isSelf ? "1.5rem" : "1rem")};
    color: ${({ theme: { palette } }) => palette.textPrimary};
    background: ${({ isSelf, theme: { palette } }) => (isSelf ? palette.background : "transparent")};
    box-shadow: ${({ isSelf }) => (isSelf ? "0 0.75rem 1.5rem 0 #0000000f" : "none")};
  }
`;

export const ChatTextarea = styled.div`
  display: flex;
  align-items: end;
  gap: 0.5rem;
  border: 1px solid ${({ theme: { input } }) => input.borderColor};
  border-radius: 0.25rem;
  height: 4.5rem;
  padding: 0.5rem;
  box-sizing: border-box;

  textarea {
    border: none;
    padding: 0;

    &::placeholder {
      color: ${({ theme: { input } }) => input.placeholderColor};
    }
  }
`;

export const ChatFooter = styled.div<{ isFirstMessage?: boolean }>`
  display: block;
  flex-grow: ${({ isFirstMessage }) => (isFirstMessage ? 1 : undefined)};

  ${ChatTextarea} {
    margin-top: ${({ isFirstMessage }) => (isFirstMessage ? "-82px" : undefined)};
  }
`;

export const SubmitButton = styled(IconButton as AnyStyledComponent)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background-color: ${({ theme: { palette } }) => palette.elementDark};
  border-radius: 1.5rem;
  font-size: 0.875rem;
  transform: rotate(90deg);

  &:hover {
    background-color: ${({ theme: { palette } }) => palette.elementDeep};
  }
`;
